import type { LucideIcon } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { MarqueeText } from '@shared/ui/primitives/MarqueeText';
import { shouldRenderTileIconAsImage } from '@shared/lib/tileIconRender';

interface TileIconProps {
  imageUrl?: string | null;
  label: string;
  /** Lucide fallback when there is no module image. */
  fallbackIcon?: LucideIcon;
  className?: string;
}

/**
 * Module / quick-link glyph. Stroke-only module SVGs from the API are tinted
 * with primary via CSS mask; uploaded or CDN SVGs render as images so fills
 * and detail are preserved.
 */
export function TileIcon({ imageUrl, label, fallbackIcon: FallbackIcon, className }: TileIconProps): JSX.Element {
  const url = imageUrl?.trim();
  if (url) {
    if (shouldRenderTileIconAsImage(url)) {
      return (
        <img
          src={url}
          alt=""
          aria-hidden
          className={cn('h-8 w-8 shrink-0 object-contain opacity-85', className)}
        />
      );
    }

    // Painted like primary text (--primary-text), so a very light primary stays visible on white.
    return (
      <span
        aria-hidden
        className={cn('inline-block h-8 w-8 shrink-0 bg-[color:var(--primary-text,var(--primary-600))] opacity-85', className)}
        style={{
          WebkitMaskImage: `url("${url}")`,
          maskImage: `url("${url}")`,
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
        }}
      />
    );
  }

  if (FallbackIcon) {
    return <FallbackIcon className={cn('h-8 w-8 shrink-0 text-primary-600 opacity-85', className)} strokeWidth={1.75} />;
  }

  return (
    <span className={cn('font-display text-headline font-semibold leading-none text-primary-600', className)}>
      {label.charAt(0)}
    </span>
  );
}

/**
 * Shared home-grid tile chrome — the same card surface, hairline and shadow as
 * the home widget cards, so every bordered thing on Home looks alike.
 */
export const homeTileButtonClass =
  'flex h-full w-full min-w-0 flex-col items-center gap-2 rounded-xl bg-card hairline px-1 py-3.5 text-center shadow-sm transition-transform duration-fast active:scale-[0.96]';

/** Shared home-grid tile label — soft, lower-weight caption text. */
export const homeTileLabelClass =
  'block w-full min-w-0 text-center text-caption font-normal leading-tight text-ink-secondary';

export function TileLabel({
  label,
  className,
}: {
  label: string;
  className?: string;
}): JSX.Element {
  return <MarqueeText className={cn(homeTileLabelClass, className)}>{label}</MarqueeText>;
}
